<template>
  <div class="mapping">
    <MessageBox class="intro">
      Select your ALM user account so that user-related properties of sticky notes (e.g. the
      assignee) sync correctly between piplanning.io and the ALM tool.
    </MessageBox>

    <div class="mapping-list">
      <div v-for="mapping in mappings" :key="mapping.sessionId" class="mapping-card">
        <div class="card-connection">
          <div class="conn-icon">
            <SvgIcon name="general/alm-connection" width="20" height="20" />
          </div>
          <div class="conn-info">
            <span class="conn-type">{{ mapping.type }}</span>
            <span class="conn-name">{{ mapping.name }}</span>
          </div>
        </div>

        <div class="card-user">
          <div v-if="mapping.error" class="error-text">
            Error loading user from {{ mapping.type }}. Check configuration.
          </div>

          <template v-else-if="editedMapping === mapping">
            <SearchSelect
              :search="searchAlmUsersFor"
              display-key="almName"
              placeholder="Type to search users..."
              autofocus
              @select="select(mapping, $event)"
              @close="closeSearch(mapping)"
            >
              <template #item="{ item }">
                <div class="option">
                  <span>{{ item.almName }}</span>
                  <span v-if="isUsed(item)" class="option-sub">
                    Already used by {{ item.pipName }}
                  </span>
                </div>
              </template>
            </SearchSelect>
          </template>

          <template v-else>
            <button
              type="button"
              class="user-pick"
              :class="{ unassigned: !mapping.almId }"
              @click="openSearch(mapping)"
            >
              <div class="user-pick-content">
                <SvgIcon
                  v-if="mapping.almId"
                  name="persona/user"
                  width="16"
                  height="16"
                  class="user-pick-icon"
                />
                <span class="user-pick-label">{{ almUserName(mapping) }}</span>
              </div>
              <SvgIcon
                name="system/chevron-down"
                width="14"
                height="14"
                class="user-pick-chevron"
              />
            </button>
            <span v-if="isUsed(mapping)" class="user-sub">
              Already used by {{ mapping.pipName }}
            </span>
          </template>
        </div>
      </div>
    </div>

    <MessageBox v-if="isAnyUsed" type="warning" class="warning">
      You assigned some ALM accounts to yourself that another user already claimed. Clicking "Save"
      will remove their assignment.
    </MessageBox>
  </div>
</template>

<script>
import { mapState } from "pinia";

import MessageBox from "@/components-ng/MessageBox.vue";
import SearchSelect from "@/components-ng/SearchSelect.vue";
import SvgIcon from "@/components-ng/SvgIcon.vue";
import { UserHandler } from "@/handlers/userHandler";
import { useAppStore } from "@/stores";
import { appUrl } from "@/utils/url";

export default {
  name: "UserMapping",
  components: { MessageBox, SearchSelect, SvgIcon },
  props: {
    savedMappings: { type: Array, required: true },
  },
  emits: ["dirty-change", "single-select-ready", "update:savedMappings"],
  data() {
    return {
      mappings: this.savedMappings.map((mapping) => ({ ...mapping })),
      editedMapping: null,
      isSingleSelect: false,
      noUser: {
        almId: "",
        almName: "-- Not mapped --",
      },
      pipUserCache: {},
    };
  },
  computed: {
    ...mapState(useAppStore, ["user", "company", "session"]),
    userHandler() {
      return new UserHandler();
    },
    isAnyUsed() {
      return this.mappings.some((mapping) => this.isUsed(mapping));
    },
  },
  mounted() {
    this.isSingleSelect = Boolean(this.$route.query.mapping) && this.mappings.length === 1;
    this.$emit("single-select-ready", this.isSingleSelect);
    if (this.isSingleSelect) {
      this.openSearch(this.mappings[0]);
    }
    return Promise.all(this.mappings.map((mapping) => this.resolvePipUser(mapping)));
  },
  methods: {
    async resolvePipUser(mapping) {
      if (this.isUsed(mapping)) {
        const id = mapping.pipId;
        mapping.pipName = this.pipUserCache[id] || "";
        if (!mapping.pipName) {
          const res = await this.userHandler.getUser(id);
          this.pipUserCache[id] = res.data?.name || id;
          if (mapping.pipId === id) {
            mapping.pipName = this.pipUserCache[id];
          }
        }
      }
      return mapping;
    },
    async searchAlmUsersFor(query) {
      const mapping = this.editedMapping;
      if (!mapping) return [];
      const almUsers = await mapping.almHandler.searchAlmUsers(query);
      const results = await Promise.all(almUsers.map((user) => this.resolvePipUser(user)));
      return [...results, this.noUser];
    },
    select(mapping, almUser) {
      mapping.almId = almUser.almId;
      mapping.almName = almUser.almName;
      mapping.pipId = almUser.pipId;
      mapping.pipName = almUser.pipName;
      this.editedMapping = null;
      this.$emit("dirty-change", true);
    },
    isUsed(mapping) {
      return mapping.pipId && mapping.pipId !== this.user.id;
    },
    openSearch(mapping) {
      this.editedMapping = mapping;
    },
    closeSearch(mapping) {
      if (this.editedMapping === mapping) {
        this.editedMapping = null;
      }
    },
    almUserName(mapping) {
      if (mapping.almId === this.noUser.almId) {
        return this.noUser.almName;
      }
      if (!mapping.almId) {
        return "Please select a user";
      }
      if (!mapping.almName) {
        return `User not found in ${mapping.type}. ID: ${mapping.almId}`;
      }
      return mapping.almName;
    },
    save() {
      const saved = this.mappings.map((mapping) => ({ ...mapping }));
      return Promise.all(
        saved.map((mapping) => {
          if (mapping.almId !== undefined && mapping.almId !== null) {
            return mapping.almHandler.setAlmUser(mapping.almId);
          }
          return undefined;
        }),
      ).then(() => {
        const me = { pipId: this.user.id, pipName: this.user.name };
        saved.forEach((mapping, i) => {
          if (mapping.almId) {
            Object.assign(mapping, me);
            if (this.mappings[i].almId === mapping.almId) {
              Object.assign(this.mappings[i], me);
            }
          }
        });
        const isDirty = this.mappings.some((mapping, i) => mapping.almId !== saved[i].almId);
        this.$emit("update:savedMappings", saved);
        this.$emit("dirty-change", isDirty);
        return isDirty;
      });
    },
    saveAndBack() {
      return this.save().then((isDirty) => {
        if (!isDirty) {
          window.location.assign(appUrl(this.$route.query.path));
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

.mapping {
  max-width: 720px;
}

.intro {
  margin-bottom: 20px;
}

.mapping-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mapping-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: c.$background-ui;
  border: 1px solid c.$border-subtle;
  border-radius: 8px;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: c.$border;
  }
}

.card-connection {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 200px;
  flex-shrink: 0;
}

.conn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: c.$background-grey-light;
  color: c.$text-subtle;
  flex-shrink: 0;
}

.conn-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.conn-type {
  @include typography.small("semi-bold");

  color: c.$text;
  text-transform: capitalize;
}

.conn-name {
  @include typography.label("regular");

  color: c.$text-subtle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-user {
  flex: 1;
  min-width: 0;
  padding-top: 2px;

  :deep(.search-select) {
    margin-bottom: 0;
  }
}

.user-pick {
  @include typography.small;

  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 36px;
  padding: 0 12px;
  font-family: inherit;
  color: c.$text;
  background: c.$background-grey-light;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    background: c.$background-ui;
    border-color: c.$border;
  }

  &:focus-visible {
    outline: 2px solid c.$main;
    outline-offset: 1px;
  }

  &.unassigned {
    border-style: dashed;
    border-color: c.$border;
    background: c.$background-ui;
    color: c.$text-subtle;
  }
}

.user-pick-content {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.user-pick-icon {
  color: c.$text-subtle;
  flex-shrink: 0;
}

.user-pick-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-pick-chevron {
  color: c.$text-subtle;
  flex-shrink: 0;
}

.user-sub {
  @include typography.label("regular");

  display: block;
  margin-top: 6px;
  color: c.$orange;
}

.option {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-sub {
  @include typography.label("regular");

  color: c.$text-subtle;
}

.error-text {
  @include typography.label;

  color: c.$brand-danger;
}

.warning {
  margin-top: 16px;
}
</style>
